import { CSSTransition } from 'react-transition-group';

import { Icon as BlueIcon } from '@blueprintjs/core';
import { Dispatch, SetStateAction, useContext } from 'react';

import AvatarDefaultImg from '@assets/svg/avatar-placeholder.svg';
import { Image } from '@mantine/core';

import { State, useState } from '@hookstate/core';
import { getUserMembers } from '@services/queries/studios';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { patchEquipment } from '@services/queries/equipments';
import { AssetContext } from '@pages/Asset/Asset.query';
import { useLocalStorage } from '@hooks';
import { ChangeAssigneeLoading } from './ChangeAssignee.loading';

import {
  Block,
  Avatar,
  Close,
  Divider,
  Header,
  Icon,
  InputContainer,
  InputSearch,
  Item,
  Items,
  Label,
  SearchIconContainer,
  Title,
  Remove,
  ItemNoFeedback,
  UsersSeparator,
  SearchCloseIconContainer,
} from './ChangeAssignee.styles';

interface IRecentUser {
  id: string;
  date: Date;
}

interface IChangeAssignee {
  open: State<boolean>;
  setOverflow?: Dispatch<SetStateAction<boolean>>;
}

export const ChangeAssignee = ({ open, setOverflow }: IChangeAssignee) => {
  const { equipmentQuery } = useContext(AssetContext);

  const params = useParams<{ id: string; equipmentId: string }>();

  const [recentUsers, setRecentUsers] = useLocalStorage<IRecentUser[]>(
    'recentAssigneeUsers',
    [],
  );

  const equipment = equipmentQuery?.data;

  const queryClient = useQueryClient();

  const searchLocal = useState<string>('');
  const searchUser = useState<string>('');

  const mutation = useMutation(patchEquipment, {
    onSuccess: (_, { data: { userId } }) => {
      queryClient.invalidateQueries(['equipments', params.equipmentId]);
      open.set(false);
      setOverflow && setOverflow(false);

      if (userId) {
        setRecentUsers(
          [
            { id: userId, date: new Date() },
            ...recentUsers.filter(item => item.id !== userId),
          ].slice(0, 5),
        );
      }
    },
  });

  const { data, isLoading, isError } = useQuery('members', getUserMembers);

  if (!equipment) {
    return <div />;
  }

  const findCurrentUser = data?.find(item => item.id === equipment.user?.id);

  return (
    <CSSTransition
      in={open.get()}
      timeout={300}
      classNames="my-node"
      unmountOnExit
      onEnter={() => open.set(true)}
      onExited={() => open.set(false)}
    >
      <Block className="asset">
        <Header>
          <Title>{equipment.user ? 'Change Assignee' : 'Assign to'}</Title>
          <Close
            onClick={() => {
              open.set(false);
              setOverflow && setOverflow(false);
            }}
          >
            <BlueIcon icon="cross" color="#8F95B2" />
          </Close>
        </Header>
        <Divider />

        <InputContainer>
          <SearchIconContainer>
            <BlueIcon icon="search" color="#8F95B2" size={12} />
          </SearchIconContainer>
          <InputSearch
            placeholder="User name or Email"
            value={searchLocal.get()}
            onChange={e => searchLocal.set(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                searchUser.set(searchLocal.get());
              }
            }}
          />
          {searchUser.get() && (
            <SearchCloseIconContainer
              onClick={() => {
                searchUser.set('');
                searchLocal.set('');
              }}
            >
              <BlueIcon icon="small-cross" color="#8F95B2" />
            </SearchCloseIconContainer>
          )}
        </InputContainer>

        {isLoading && <ChangeAssigneeLoading />}

        {!isLoading && (
          <Items className="asset bscroll">
            {findCurrentUser && (
              <>
                <Label className="h1">Current Assignee</Label>
                <ItemNoFeedback className="asset">
                  <Icon>
                    <Image
                      src={findCurrentUser.picture || AvatarDefaultImg}
                      alt=""
                      radius={12}
                      width="24px"
                      height="24px"
                      placeholder={
                        <Avatar
                          className="defaultImage"
                          src={AvatarDefaultImg}
                          alt=""
                        />
                      }
                    />
                  </Icon>
                  <Label>{findCurrentUser.name}</Label>
                  <Remove
                    type="button"
                    onClick={() => {
                      mutation.mutate({
                        id: params.equipmentId,
                        data: { userId: null },
                      });
                    }}
                  >
                    <BlueIcon icon="cross" color="#D14343" size={12} />
                  </Remove>
                </ItemNoFeedback>
              </>
            )}

            {!isLoading &&
              !isError &&
              data &&
              (() => {
                const filteredUsers = data
                  .filter(item => item.id !== equipment.user?.id)
                  .filter(item =>
                    searchUser.get()
                      ? item.name
                          ?.toLowerCase()
                          .includes(searchUser.get().toLowerCase()) ||
                        item.email
                          ?.toLowerCase()
                          .includes(searchUser.get().toLowerCase())
                      : true,
                  );

                const showRecent =
                  filteredUsers.filter(
                    item =>
                      !!recentUsers.find(userItem => userItem.id === item.id),
                  ).length > 0;

                const showNoRecent =
                  filteredUsers.filter(
                    item =>
                      !recentUsers.find(userItem => userItem.id === item.id),
                  ).length > 0;

                const showSeparator = showRecent && showNoRecent;

                return (
                  <>
                    {findCurrentUser && (showRecent || showNoRecent) && (
                      <UsersSeparator />
                    )}
                    {showRecent && <Label className="h1">Recent</Label>}
                    {recentUsers
                      .map(item =>
                        filteredUsers.find(userItem => userItem.id === item.id),
                      )
                      .filter(
                        item =>
                          !!recentUsers.find(
                            userItem => userItem.id === item?.id,
                          ),
                      )
                      .map(item => (
                        <Item
                          key={item?.id}
                          onClick={() => {
                            mutation.mutate({
                              id: params.equipmentId,
                              data: { userId: item?.id },
                            });
                          }}
                        >
                          <Icon>
                            <Image
                              src={item?.picture || AvatarDefaultImg}
                              alt=""
                              radius={12}
                              width="24px"
                              height="24px"
                              placeholder={
                                <Avatar
                                  className="defaultImage"
                                  src={AvatarDefaultImg}
                                  alt=""
                                />
                              }
                            />
                          </Icon>
                          <Label>{item?.name}</Label>
                        </Item>
                      ))}

                    {showRecent && showSeparator && <UsersSeparator />}

                    {filteredUsers
                      .filter(
                        item =>
                          !recentUsers.find(
                            userItem => userItem.id === item.id,
                          ),
                      )
                      .map(item => (
                        <Item
                          key={item.id}
                          onClick={() => {
                            mutation.mutate({
                              id: params.equipmentId,
                              data: { userId: item.id },
                            });
                          }}
                        >
                          <Icon>
                            <Image
                              src={item.picture || AvatarDefaultImg}
                              alt=""
                              radius={12}
                              width="24px"
                              height="24px"
                              placeholder={
                                <Avatar
                                  className="defaultImage"
                                  src={AvatarDefaultImg}
                                  alt=""
                                />
                              }
                            />
                          </Icon>
                          <Label>{item.name}</Label>
                        </Item>
                      ))}
                  </>
                );
              })()}
          </Items>
        )}
      </Block>
    </CSSTransition>
  );
};
